import {
  Component,
  Input,
  OnInit
} from '@angular/core';
import { map, of, switchMap, tap } from 'rxjs';
import { PersonUtils } from 'src/app/auth_profile/utils/v3/person-utils';
import * as LessonsV3 from 'src/app/col/model/v3/lessons';
import { NativeServiceApiProvider } from 'src/app/services/native-api-provider.service';
import { Page, Pageable } from 'src/app/utils/pageable';

@Component({
    selector: 'app-lesson-history',
    templateUrl: './lesson-history.component.html',
    styleUrls: ['./lesson-history.component.scss'],
    standalone: false
})
export class LessonHistoryComponent implements OnInit {

  private readonly historyStatuses: LessonsV3.LessonStatus[] = [
    LessonsV3.LessonStatus.Complete,
    LessonsV3.LessonStatus.Booked,
    LessonsV3.LessonStatus.Due,
    LessonsV3.LessonStatus.InvalidatedByStudent,
    LessonsV3.LessonStatus.InvalidatedByTeacher,
  ];

  private _teacherId: number;
  private _lessonId: number;
  private _studentId: number;
  private _productCode: string;

  pageable: Pageable;
  results: Page<LessonsV3.LFViewBase>;

  @Input()
  set teacherId(id: number) {
    this._teacherId = id;
    this.refreshHistory();
  }
  @Input()
  set lessonId(id: number) {
    this._lessonId = id;
    this.refreshHistory();
  }
  @Input()
  set studentId(id: number) {
    this._studentId = id;
    this.refreshHistory();
  }
  @Input()
  set productCode(code: string) {
    this._productCode = code;
    this.refreshHistory();
  }

  constructor(private provider: NativeServiceApiProvider) {
    this.pageable = new Pageable(0, 5, [
      'metric.details.plannedStartDate,DESC',
    ]);
  }

  ngOnInit(): void {}

  loadLessonHistoryPage(pageable: Pageable) {
    if (!this.canLoadHistory())
      return of(null);
    return this.provider.colV3().pipe(
      switchMap((api) =>
        api.findLessons(
          LessonsV3.LessonCategory.Col,
          null,
          'teacher-full',
          null,
          this._studentId,
          null,
          null,
          null,
          this._productCode,
          this.historyStatuses,
          null,
          // 48 hours ahead, as the legacy related-lessons endpoint did, so the history also
          // shows the student's lessons already scheduled for the next two days
          new Date(Date.now() + 48 * 60 * 60 * 1000),
          pageable
        )
      ),
      map((lessonHistoryPage) => this.filterCurrentLesson(lessonHistoryPage)),
      tap((lessonHistoryPage) => {
        this.results = lessonHistoryPage;
      })
    );
  }

  private filterCurrentLesson(
    lessonHistoryPage: Page<LessonsV3.LFViewBase>
  ): Page<LessonsV3.LFViewBase> {
    const content = (lessonHistoryPage?.content ?? []).filter(
      (lesson) => this.extractLessonId(lesson) !== this._lessonId
    );

    return {
      ...lessonHistoryPage,
      content,
      totalElements: content.length,
    } as Page<LessonsV3.LFViewBase>;
  }

  private extractLessonId(lesson: LessonsV3.LFViewBase): number | null {
    const candidate = (lesson as Partial<LessonsV3.LessonRef>).id;
    return typeof candidate === 'number' ? candidate : null;
  }

  private getLessonMetric(lesson: LessonsV3.LFViewBase): LessonsV3.V3LessonMetric | null {
    const candidate = (lesson as Partial<LessonsV3.LessonBase<LessonsV3.V3LessonMetric>>)
      .lessonMetric;
    return candidate ?? null;
  }

  getLessonType(lesson: LessonsV3.LFViewBase): string {
    const type = (lesson as Partial<LessonsV3.LessonBase<LessonsV3.V3LessonMetric>>).type;
    return type ?? '';
  }

  getHistoryDate(lesson: LessonsV3.LFViewBase) {
    const lessonMetric = this.getLessonMetric(lesson);
    if (!lessonMetric) return null;
    return lessonMetric.started || lessonMetric.plannedStartDate;
  }

  private getLessonProgress(
    lesson: LessonsV3.LFViewBase
  ): Partial<LessonsV3.CallanProgressDetails> {
    const progress = (lesson as Partial<LessonsV3.LFPreviewWithFullProgress>).progressCommited;
    return (progress ?? {}) as Partial<LessonsV3.CallanProgressDetails>;
  }

  getStartPage(lesson: LessonsV3.LFViewBase): number | string {
    const progress = this.getLessonProgress(lesson);
    return progress.start ?? '-';
  }

  getNwp(lesson: LessonsV3.LFViewBase): number | string {
    const progress = this.getLessonProgress(lesson);
    return progress.nwp ?? '-';
  }

  getReading(lesson: LessonsV3.LFViewBase): number | string {
    const progress = this.getLessonProgress(lesson);
    return progress.reading ?? '-';
  }

  getHeadwords(lesson: LessonsV3.LFViewBase): string {
    const progress = this.getLessonProgress(lesson);
    return progress.headwords || '-';
  }

  getMessage(lesson: LessonsV3.LFViewBase): LessonsV3.ApiLessonMessage {
    return (
      (lesson as Partial<LessonsV3.LFWithMessage>).message ?? {
        lessonComment: '',
        privateLessonComment: '',
        message: '',
      }
    );
  }

  getStudentMessage(lesson: LessonsV3.LFViewBase): string {
    return (lesson as Partial<LessonsV3.LFWithStudentMessage>).studentMessage ?? '';
  }

  getFlags(lesson: LessonsV3.LFViewBase): LessonsV3.ApiLessonFlag[] {
    return (lesson as Partial<LessonsV3.LFWithFlags>).flags ?? [];
  }

  getTeacherName(lesson: LessonsV3.LFViewBase) {
    const teacher = (lesson as Partial<LessonsV3.LFWithTeacher<LessonsV3.Teacher>>).teacher;
    const teacherPerson = teacher?.reference?.person;
    if (!teacherPerson) {
      return '';
    }
    return PersonUtils.getPersonName(teacherPerson);
  }

  getFlagDescription(type: LessonsV3.LessonFlagType | string) {
    switch (type) {
      case LessonsV3.LessonFlagType.ClassroomIssue:
        return 'Classroom issue';
      case LessonsV3.LessonFlagType.Other:
        return 'Other';
      case LessonsV3.LessonFlagType.PoorConnection:
        return 'Poor connection';
      case LessonsV3.LessonFlagType.StudentNeedsHelp:
        return 'Student neeeds help';
      case LessonsV3.LessonFlagType.StudentNotShow:
        return 'Student no Show';
      case LessonsV3.LessonFlagType.TeacherNotShow:
        return 'Teacher no show';
      default:
        return 'Unknown';
    }
  }

  onPageChange(pageable: Pageable) {
    this.pageable = pageable;
    this.refreshHistory(pageable);
  }

  getHistory() {
    return this.results?.content;
  }

  private refreshHistory(pageable: Pageable = this.pageable): void {
    this.loadLessonHistoryPage(pageable).subscribe();
  }

  private canLoadHistory(): boolean {
    return !!(this._teacherId && this._lessonId && this._studentId && this._productCode);
  }
}
